import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { ClassValue } from 'clsx'

export function cn(...inputs: Array<ClassValue>) {
  return twMerge(clsx(inputs))
}

/**
 * Formats a phone number to international format with spaces: +1 425 577 8216
 * Handles various input formats including:
 * - +14255778216
 * - 14255778216
 * - 4255778216
 * - (425) 577-8216
 * - 425-577-8216
 * - 425.577.8216
 */
export function formatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return ''

  // Remove all non-digit characters except leading +
  const hasPlus = phone.startsWith('+')
  const digits = phone.replace(/\D/g, '')

  if (digits.length === 0) return phone

  // Normalize to 11 digits with country code
  let normalized = digits
  if (digits.length === 10) {
    // Assume US number, add country code
    normalized = `1${digits}`
  } else if (digits.length === 11 && digits.startsWith('1')) {
    // Already has US country code
    normalized = digits
  } else if (digits.length > 11) {
    // May have extra digits, try to use as-is
    normalized = digits
  } else {
    // Less than 10 digits or non-US, return as-is with basic formatting
    return hasPlus ? `+${digits}` : digits
  }

  // Format as +X XXX XXX XXXX for 11-digit numbers
  if (normalized.length === 11) {
    return `+${normalized[0]} ${normalized.slice(1, 4)} ${normalized.slice(4, 7)} ${normalized.slice(7)}`
  }

  // For other lengths, just add + prefix and return
  return `+${normalized}`
}

/**
 * Normalizes a phone number to E.164 format for API calls: +12067187603
 * No spaces, just + and digits.
 */
export function normalizePhoneToE164(phone: string | null | undefined): string {
  if (!phone) return ''

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '')

  if (digits.length === 0) return ''

  // Normalize to 11 digits with country code
  if (digits.length === 10) {
    // Assume US number, add country code
    return `+1${digits}`
  } else if (digits.length === 11 && digits.startsWith('1')) {
    // Already has US country code
    return `+${digits}`
  }

  // For other formats, assume already has country code
  return `+${digits}`
}
